import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTable, useSortBy } from "react-table";
import "./customReactTable.css";

// Fallback row height for `virtualize` until the first real row is measured.
const DEFAULT_ROW_HEIGHT = 37;

export const CustomReactTable = (props) => {
  const {
    data,
    columns,
    tableLoading,
    type,
    containerClassName = "",
    tableClassName = "",
    containerRef,
    // Virtual scrolling for very long lists: only the rows inside the
    // scroll viewport (plus `overscan` above/below) are put in the DOM, with
    // spacer rows standing in for the rest, so scrolling stays smooth with
    // hundreds of thousands of rows. Sorting still runs over all `data`.
    virtualize = false,
    overscan = 12,
  } = props;

  const table = useTable({ columns, data }, useSortBy);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    table;

  const scrollElRef = useRef(null);
  const frameRef = useRef(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(800);
  const [rowHeight, setRowHeight] = useState(DEFAULT_ROW_HEIGHT);

  const setScrollEl = useCallback(
    (node) => {
      scrollElRef.current = node;
      if (typeof containerRef === "function") containerRef(node);
      else if (containerRef) containerRef.current = node;
    },
    [containerRef]
  );

  const handleScroll = useCallback(
    (event) => {
      if (!virtualize) return;
      const top = event.currentTarget.scrollTop;
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => setScrollTop(top));
    },
    [virtualize]
  );

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  useEffect(() => {
    const el = scrollElRef.current;
    if (!virtualize || !el) return undefined;
    const update = () => setViewportHeight(el.clientHeight || 800);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [virtualize]);

  // Measure a real rendered row so the spacer heights match the CSS. Runs
  // after every render on purpose; the > 0.5px guard stops it looping.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!virtualize) return;
    const firstRow = scrollElRef.current?.querySelector("tbody tr[data-row]");
    const height = firstRow?.offsetHeight;
    if (height && Math.abs(height - rowHeight) > 0.5) setRowHeight(height);
  });

  let start = 0;
  let end = rows.length;
  if (virtualize) {
    start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    // Even start keeps the nth-child zebra striping stable while scrolling.
    start -= start % 2;
    end = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan);
  }
  const visibleRows = virtualize ? rows.slice(start, end) : rows;
  const topPad = start * rowHeight;
  const bottomPad = (rows.length - end) * rowHeight;
  const spacerCellStyle = { padding: 0, border: 0, background: "transparent" };

  return (
    <div className={`tabFix ${containerClassName}`} onScroll={handleScroll} ref={setScrollEl}>
      {/* Apply the table props */}
      <table className={`tabNew ${tableClassName}`} {...getTableProps()}>
        <thead className="table_head_style">
          {headerGroups?.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup?.headers?.map((column) => (
                // Aplicamos las propiedades de ordenación a cada columna
                <th
                  {...column?.getHeaderProps(column?.getSortByToggleProps())}
                  data-col={column.id}
                  className={
                    column.isSorted
                      ? column.isSortedDesc
                        ? "desc"
                        : "asc"
                      : ""
                  }
                >
                  {column?.render("Header")}
                  <span>
                    {column?.isSorted
                      ? column?.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {/* Apply the table body props */}

        <tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            tableLoading || rows?.length <= 0 ? (
              <tr>
                {tableLoading ? (
                  <td colSpan={columns.length} className="span_of_table">
                    <div
                      className={
                        type === "fullVoter" ? "loaderGifleft" : "loaderGif"
                      }
                    >
                      {" "}
                      {/* Loading ... */}
                      {/* <img src={mytruck} alt="" /> */}
                    </div>
                  </td>
                ) : (
                  <td
                    colSpan={columns.length}
                    className={
                      type === "fullVoter" ? "span_of_table2" : "span_of_table"
                    }
                  >
                    No Data
                  </td>
                )}
              </tr>
            ) : (
              <>
                {virtualize && (
                  <tr aria-hidden="true" style={{ height: topPad }}>
                    <td colSpan={columns.length} style={spacerCellStyle} />
                  </tr>
                )}
                {visibleRows.map((row) => {
                  // Prepare the row for display
                  prepareRow(row);
                  return (
                    // Apply the row props
                    <tr {...row?.getRowProps()} data-row="">
                      {
                        // Loop over the rows cells
                        row?.cells.map((cell) => {
                          // Apply the cell props
                          return (
                        <td {...cell.getCellProps()} data-col={cell.column.id}>
                              {
                                // Render the cell contents
                                cell.render("Cell")
                              }
                            </td>
                          );
                        })
                      }
                    </tr>
                  );
                })}
                {virtualize && bottomPad > 0 && (
                  <tr aria-hidden="true" style={{ height: bottomPad }}>
                    <td colSpan={columns.length} style={spacerCellStyle} />
                  </tr>
                )}
              </>
            )
          }
        </tbody>
      </table>
    </div>
  );
};
